import React from 'react';
import { useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    IconButton,
    Link,
    Table,
    Tooltip,
    Typography,
} from '@mui/material';
import { Delete, Event } from '@mui/icons-material';
import Date from 'components/formaters/Date';
import { CarModel, StockStatus } from 'models/StockModel';
import { CompanyType } from 'models/CompanyModel';
import useCars from 'modules/cars/hooks/useCars';
import DateLimiter from 'modules/cars/common/DateLimiter';
import useCarModals from 'modules/cars/hooks/useCarModals';
import useVoyagesFilter from 'modules/voyages/hooks/useVoyagesFilter';
import auth from 'recoil/atoms/auth';


type ShippingCardProps = {
    car: CarModel,
}

const cancelable = [StockStatus.Pending, StockStatus.Ready];

const ShippingCard = ({car}: ShippingCardProps) => {
    const my = useRecoilValue(auth);
    const {t} = useTranslation('cars');
    const {openYardModal, openVoyageModal, openShipDateLimitModal, confirmCancelBooking} = useCarModals();
    const {setShipDateLimit, setVoyage} = useCars();
    const filterFor = useVoyagesFilter();
    const voyages = filterFor(car);

    return (
        <Card sx={{mb: 2}}>
            <CardHeader
                title={t('shipping')}
            />

            {car.voyage && (
                <CardContent>
                    <Table sx={{
                        'td:first-of-type': {
                            color: 'text.disabled',
                            fontSize: 'smaller',
                        },
                    }}>
                        <tbody>
                        <tr>
                            <td>{t('destination')}</td>
                            <td>{car.voyage.pod.name}</td>
                        </tr>
                        <tr>
                            <td>{t('booked_on')}</td>
                            <td>{car.voyage.vessel.name} <Link
                                href={`/voyage/${car.voyage.id}`}>{car.voyage.number}</Link></td>
                        </tr>
                        <tr>
                            <td>{t('departure')}</td>
                            <td><Date date={car.voyage.etd}/></td>
                        </tr>
                        <tr>
                            <td>{t('arrival')}</td>
                            <td>
                                <Date date={car.voyage.eta}/>
                                {' '}
                                {car.shipDateLimit && (
                                    <Tooltip
                                        title={<DateLimiter limit={car.shipDateLimit}/>}
                                        placement="top"
                                    >
                                        <Event
                                            fontSize="small"
                                            color="warning"
                                            sx={{verticalAlign: 'middle'}}
                                        />
                                    </Tooltip>
                                )}
                            </td>
                        </tr>
                        </tbody>
                    </Table>

                    {car.bls && my.type === CompanyType.Master && (
                        <>
                            <Box sx={{margin: '.6rem 0'}}>{t('BL')}</Box>

                            <Table sx={{
                                'td:first-of-type': {
                                    color: 'text.disabled',
                                    fontSize: 'smaller',
                                },
                            }}>
                                <tbody>
                                {car.bls.map((bl, i) => (
                                    <tr key={bl.id}>
                                        <td>{bl.type}</td>
                                        <td>
                                            {i === 0 ?
                                                <Link href={`/voyage/${car.voyage!.id}/bl#${bl.id}`}>{bl.number}</Link>
                                                : bl.number
                                            }
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </Table>
                        </>
                    )}
                </CardContent>
            )}

            {!car.voyage && car.shipDateLimit && (
                <CardContent>
                    <Typography component="span" color="red" mr={2}>
                        {car.shipDateLimit.date === '3000-01-01' ?
                            <>{t('temporary_stop')}</> :
                            <DateLimiter limit={car.shipDateLimit} />
                        }
                    </Typography>
                    <IconButton
                        onClick={() => setShipDateLimit(car.id, car.shipDateLimit!.after, null)}
                    >
                        <Delete fontSize="small"/>
                    </IconButton>
                </CardContent>
            )}

            {(car.status === StockStatus.Pending || car.status === StockStatus.Ready) && (
            <CardActions sx={{justifyContent: 'space-between'}}>
                {!car.yard && (
                    <Button
                        onClick={() => openYardModal([car])}
                        size="small"
                    >
                        {t('select_yard')}
                    </Button>
                )}

                {car.yard && !car.voyage && my.permissions.has('booking make') && (
                    <>
                        {voyages.length === 0 && (
                            <Button
                                size="small"
                                disabled
                            >
                                {t('no_voyage_wait')}
                            </Button>
                        )}

                        {voyages.length === 1 && (
                            <Button
                                onClick={() => setVoyage([car.id], voyages[0].id)}
                                size="small"
                            >
                                {t('book_for', {voyage: voyages[0].number})}
                            </Button>
                        )}

                        {voyages.length > 1 && (
                            <Button
                                onClick={() => openVoyageModal([car])}
                                size="small"
                            >
                                {t('book_voyage')}
                            </Button>
                        )}
                    </>
                )}

                {!car.voyage && !car.shipDateLimit && my.permissions.has('cars update') && (
                    <Button
                        onClick={() => openShipDateLimitModal(car)}
                        size="small"
                    >
                        {t('limit_date')}
                    </Button>
                )}

                {car.voyage && my.permissions.has('booking cancel') && cancelable.includes(car.status) && (
                    <Button
                        onClick={() => confirmCancelBooking([car])}
                        size="small"
                    >
                        {t('cancel_booking')}
                    </Button>
                )}
            </CardActions>
            )}
        </Card>
    );
};

export default ShippingCard;
